import { Component, lazy, Suspense, useCallback, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from './components/Layout';
import Copilot from './components/Copilot';
import { NavContext } from './nav';
import type { NavParams, NavState } from './nav';
import { withChunkRecovery } from './lazyChunk';
import { trackError } from './telemetry';
import { DEFAULT_TAB, PATH_BY_TAB, contractPath, pathForTab, tabForPath } from './routes';

/* ── Pages, lazily ───────────────────────────────────────────────────────────
   Every page is its own chunk. Before phase 4 all thirteen were statically
   imported, so the entry bundle was 1.3 MB and every reader downloaded the
   contract intake modal to look at Spend Cube.

   ⚠ EVERY ONE goes through `withChunkRecovery`, and that is not optional. SWA
   replaces hashed filenames on deploy, so a tab left open across one asks for a
   chunk that no longer exists, the dynamic import rejects, and — before the
   recovery existed — that unmounted the whole tree and read as an auth failure
   for hours while every API route returned 200. Thirteen new chunks is thirteen
   new ways to reproduce that. See lazyChunk.ts. */
const SpendCube = lazy(() => withChunkRecovery(import('./pages/SpendCube')));
const PriceIntelligence = lazy(() => withChunkRecovery(import('./pages/PriceIntelligence')));
const SupplyReviewBrief = lazy(() => withChunkRecovery(import('./pages/SupplyReviewBrief')));
const Relationships = lazy(() => withChunkRecovery(import('./pages/Relationships')));
const Supplier360 = lazy(() => withChunkRecovery(import('./pages/Supplier360')));
const PerformanceRisk = lazy(() => withChunkRecovery(import('./pages/PerformanceRisk')));
const PriceRegister = lazy(() => withChunkRecovery(import('./pages/PriceRegister')));
const Playbooks = lazy(() => withChunkRecovery(import('./pages/Playbooks')));
const SourcingPipeline = lazy(() => withChunkRecovery(import('./pages/SourcingPipeline')));
const Contracts = lazy(() => withChunkRecovery(import('./pages/Contracts')));
const ContractCoverage = lazy(() => withChunkRecovery(import('./pages/ContractCoverage')));
const Governance = lazy(() => withChunkRecovery(import('./pages/Governance')));
const DataSources = lazy(() => withChunkRecovery(import('./pages/DataSources')));

/* Keyed by `string`, not by a union of the keys below, because the tab can come
 * from a URL somebody typed — the `?? SpendCube` fallback is what makes an
 * unrecognised tab safe, and the explicit `undefined` keeps that fallback honest
 * to the compiler rather than dead-looking. Every page takes no props, so
 * `ComponentType` is the whole contract. */
const PAGES: Record<string, ComponentType | undefined> = {
  spend: SpendCube,
  price: PriceIntelligence,
  brief: SupplyReviewBrief,
  relationships: Relationships,
  supplier360: Supplier360,
  performance: PerformanceRisk,
  register: PriceRegister,
  playbooks: Playbooks,
  pipeline: SourcingPipeline,
  'contracts-register': Contracts,
  contracts: ContractCoverage,
  governance: Governance,
  'data-sources': DataSources,
};

/**
 * A page that fails to load must cost the reader the page, not the app.
 *
 * The same reasoning as DataTable's GridBoundary, one level up: with pages behind
 * lazy chunks, a render-time throw anywhere in a page would otherwise unmount the
 * whole tree and leave a blank screen with no explanation — which is exactly how
 * the 2026-08-15 stale-chunk failure presented. The nav stays on screen, so the
 * reader can go somewhere else instead of reaching for the tab close button.
 */
class PageBoundary extends Component<{ children?: ReactNode }, { failed: boolean }> {
  constructor(props: { children?: ReactNode }) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  /* Reported explicitly because catching it here is what stops App Insights'
     automatic handler from ever seeing it. A page that will not load is exactly
     the failure worth knowing about without waiting for someone to report it —
     the stale-chunk outage read as an auth failure for hours. */
  componentDidCatch(error: unknown) {
    trackError(error, 'PageBoundary');
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="panel" style={{ padding: 16 }}>
        <p><strong>This page could not be loaded.</strong></p>
        <p className="muted">
          If the app was deployed while you had it open, reloading picks up the new
          version. Everything else in the app still works — the nav on the left is live.
        </p>
        <button type="button" className="btn" style={{ marginTop: 10 }} onClick={() => window.location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

/** What a page shows while its chunk arrives. Deliberately plain: a spinner that
 *  flashes for 80ms is noise, and the nav is already painted around it. */
function PageFallback() {
  return <div className="state" style={{ padding: 16 }}>Loading…</div>;
}

/**
 * One tab's page, resolved from the URL segment.
 *
 * `contractRef` is passed through as a nav param rather than handled here,
 * because the register is what knows how to find a contract by its printed
 * reference — see the `params.contractRef` effect in pages/Contracts.tsx.
 */
function TabRoute({ tab }: { tab: string }) {
  const Page = PAGES[tab] ?? SpendCube;
  return (
    <PageBoundary key={tab}>
      <Suspense fallback={<PageFallback />}>
        <Page />
      </Suspense>
    </PageBoundary>
  );
}

/** `/:segment` — every tab except the contract deep link. */
function SegmentRoute() {
  const { segment } = useParams();
  return <TabRoute tab={tabForPath(segment)} />;
}

/** `/contracts/:ref` — the register, opened on one contract. */
function ContractRefRoute() {
  return <TabRoute tab="contracts-register" />;
}

export default function App() {
  const routerNavigate = useNavigate();
  const location = useLocation();
  const [theme, setTheme] = useState(
    () => document.documentElement.getAttribute('data-theme') || 'light',
  );
  const [copilotOpen, setCopilotOpen] = useState(false);

  const segments = location.pathname.split('/').filter(Boolean);
  const activeTab = tabForPath(segments[0]);
  /* The second segment is only meaningful under /contracts, and only there does
     it name a contract. Decoded because the ref is encoded into the URL. */
  const contractRef = segments[0] === PATH_BY_TAB['contracts-register'] && segments[1]
    ? decodeURIComponent(segments[1])
    : null;

  /**
   * The nav contract the whole app already speaks — `navigate(tab, params)` —
   * kept exactly as it was, now backed by the router.
   *
   * Params still travel in history state rather than the query string. That is
   * deliberate: they are handoffs between pages (a vendor row opening Supplier
   * 360, a mailbox proposal opening the contract form) and some carry whole
   * objects. Putting those in the address bar would make an unreadable URL that
   * breaks the moment a shape changes. The ONE param that is worth a real URL is
   * a contract reference, and it gets a real path segment.
   */
  const navigate = useCallback((tab: string, nextParams: NavParams = {}) => {
    const ref = nextParams.contractRef;
    if (tab === 'contracts-register' && typeof ref === 'string' && ref) {
      routerNavigate(contractPath(ref), { state: nextParams });
      return;
    }
    routerNavigate(pathForTab(tab), { state: nextParams });
  }, [routerNavigate]);

  const onTabChange = useCallback((tab: string) => {
    routerNavigate(pathForTab(tab));
  }, [routerNavigate]);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next = t === 'dark' ? 'light' : 'dark';
      document.documentElement.setAttribute('data-theme', next);
      localStorage.setItem('theme', next);
      return next;
    });
  }, []);

  /* History state is whatever the previous page handed over; the URL's contract
     ref is layered on top so a link pasted into the address bar behaves exactly
     like an in-app navigation. `location.state` is `unknown` by React Router's
     own typing, which matches NavParams' "values are unknown, narrow at the
     reader" rule — so it is checked rather than cast. */
  const stateParams: NavParams = location.state && typeof location.state === 'object'
    ? location.state as NavParams
    : {};
  const params = useMemo<NavParams>(
    () => (contractRef ? { ...stateParams, contractRef } : stateParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.key, contractRef],
  );

  const navValue = useMemo<NavState>(() => ({ navigate, params }), [navigate, params]);

  return (
    <NavContext.Provider value={navValue}>
      <Layout
        activeTab={activeTab}
        onTabChange={onTabChange}
        theme={theme}
        onToggleTheme={toggleTheme}
        copilotOpen={copilotOpen}
        onToggleCopilot={() => setCopilotOpen((o) => !o)}
        copilot={<Copilot activeTab={activeTab} params={params} />}
      >
        <Routes>
          <Route path="/" element={<Navigate to={pathForTab(DEFAULT_TAB)} replace />} />
          {/* Before /:segment, so a ref is not mistaken for a tab. */}
          <Route path={`/${PATH_BY_TAB['contracts-register']}/:ref`} element={<ContractRefRoute />} />
          <Route path="/:segment" element={<SegmentRoute />} />
          {/* Anything else falls back the way the old registry did, rather than
              showing a 404 for what is a single-screen internal tool. */}
          <Route path="*" element={<Navigate to={pathForTab(DEFAULT_TAB)} replace />} />
        </Routes>
      </Layout>
    </NavContext.Provider>
  );
}
